import { useCallback, useEffect, useMemo, useState } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { useNavigate } from "react-router-dom";
import { Settings2, X, Plus, ChevronRight, Check, ExternalLink, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { hentBlokkData, blokkSok, fmtFrist, fmtDue, stageDot } from "./blokkData";

const dagerTil = (d) => {
  if (!d) return null;
  const dato = new Date(`${String(d).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(dato.getTime())) return null;
  const naa = new Date();
  naa.setHours(12, 0, 0, 0);
  return Math.round((dato - naa) / 86400000);
};

const PRI_RANG = { P1: 0, P2: 1, P3: 2 };
const ukeSort = (a, b) => {
  const g = (r) => {
    const d = dagerTil(r.frist);
    if (d === null) return 5;
    if (d < 0) return 0;
    if (d === 0) return 1;
    if (d <= 1) return 2;
    if (d <= 6) return 3;
    return 4;
  };
  return g(a) - g(b) || (PRI_RANG[a.pri] ?? 9) - (PRI_RANG[b.pri] ?? 9) || String(a.frist || "9999").localeCompare(String(b.frist || "9999"));
};

const koblingRute = (k) => {
  if (!k) return null;
  if (k.type === "speaker") return `/admin/speakers?aapne=${k.id}`;
  if (k.type === "partner") return `/admin/partners?aapne=${k.id}`;
  return null;
};

function UkePanel({ rad, onLukk, onFullfor, onGjenaapne, navigate }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);
  const due = fmtDue(rad.frist);
  const rute = koblingRute(rad.kobling);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="dok-uke-panel">
      <div className="absolute inset-0 bg-black/20" onClick={onLukk} />
      <aside className="dok-uke-drawer relative w-full max-w-[340px] h-full bg-white border-l border-[#E4E4E1] shadow-2xl flex flex-col">
        <div className="px-5 pt-4 pb-3 border-b border-neutral-100 flex items-start justify-between gap-3">
          <p className="text-[14.5px] font-semibold leading-snug" data-testid="dok-uke-panel-tittel">{rad.navn}</p>
          <button type="button" onClick={onLukk} className="text-neutral-400 hover:text-neutral-800 mt-0.5" data-testid="dok-uke-panel-lukk"><X size={15} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-[12.5px]">
          <div className="flex items-center justify-between">
            <span className="dok-uke-panel-label">Owner</span>
            {rad.ansvarlig
              ? <span className="inline-flex items-center gap-1.5 font-medium">
                  {rad.eierBilde
                    ? <i className="dok-eier-mini foto"><img src={rad.eierBilde} alt="" /></i>
                    : <i className="dok-eier-mini" style={{ background: rad.eierFarge || "#8B5CF6" }}>{rad.ansvarlig[0]}</i>}
                  {rad.ansvarlig}
                </span>
              : <span className="text-amber-700">Unassigned</span>}
          </div>
          <div className="flex items-center justify-between">
            <span className="dok-uke-panel-label">Due</span>
            <span className={due?.tone === "overdue" ? "text-red-600 font-medium" : due?.tone === "today" ? "text-amber-700 font-medium" : ""}>
              {rad.frist ? `${fmtFrist(rad.frist)}${due && due.tone ? ` · ${due.label}` : ""}` : "—"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="dok-uke-panel-label">Priority</span>
            <span>{rad.pri || "—"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="dok-uke-panel-label">Status</span>
            <span className="inline-flex items-center gap-1.5"><i className="dok-blokk-dot" style={{ background: stageDot(rad.stage) }} />{rad.stageLabel}</span>
          </div>
          {rad.kobling?.label && (
            <div className="pt-2 border-t border-neutral-100">
              <p className="dok-uke-panel-label mb-1.5">Linked to</p>
              <p className="font-medium text-[13px]">{rad.kobling.label}</p>
              {rute && (
                <button type="button" onClick={() => navigate(rute)}
                  className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-neutral-600 hover:text-neutral-900 transition-colors"
                  data-testid="dok-uke-panel-record">
                  Åpne {rad.kobling.type === "partner" ? "partner" : "speaker"} record <ChevronRight size={11} />
                </button>
              )}
            </div>
          )}
          {rad.nesteSteg && (
            <div className="pt-2 border-t border-neutral-100">
              <p className="dok-uke-panel-label mb-1">Neste delsteg</p>
              <p className="text-neutral-600">{rad.nesteSteg}</p>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-neutral-100 space-y-2">
          {rad.ferdig ? (
            <button type="button" onClick={() => onGjenaapne(rad.id)}
              className="w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold bg-white border border-[#E4E4E1] text-neutral-700 rounded-lg py-2.5 hover:border-neutral-400 transition-colors"
              data-testid="dok-uke-panel-gjenaapne">
              Gjenåpne oppgaven
            </button>
          ) : (
            <button type="button" onClick={() => onFullfor(rad.id)}
              className="w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold bg-neutral-900 text-white rounded-lg py-2.5 hover:bg-neutral-700 transition-colors"
              data-testid="dok-uke-panel-fullfor">
              <Check size={13} /> Marker som ferdig
            </button>
          )}
          <button type="button" onClick={() => navigate(`/admin/saker?aapne=${rad.id}`)}
            className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-medium text-neutral-500 hover:text-neutral-900 transition-colors"
            data-testid="dok-uke-panel-sak">
            <ExternalLink size={11} /> Åpne i Saker
          </button>
        </div>
      </aside>
    </div>
  );
}

function NyUkeRad({ onNy }) {
  const [aapen, setAapen] = useState(false);
  const [tekst, setTekst] = useState("");
  if (!aapen) {
    return (
      <button type="button" className="dok-uke-ny" onClick={() => setAapen(true)} data-testid="dok-uke-ny-btn">
        <Plus size={11} /> Legg til
      </button>
    );
  }
  const send = () => {
    if (tekst.trim()) onNy(tekst.trim());
    setTekst("");
    setAapen(false);
  };
  return (
    <div className="dok-uke-nyform">
      <input autoFocus value={tekst} onChange={(e) => setTekst(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") send(); if (e.key === "Escape") { setAapen(false); setTekst(""); } }}
        onBlur={() => { if (!tekst.trim()) setAapen(false); }}
        placeholder="Ny oppgave — Enter lagrer" data-testid="dok-uke-ny-input" />
    </div>
  );
}

function UkeVisning({ rader, onFullfor, onGjenaapne, onNy, navigate }) {
  const [eier, setEier] = useState(null);
  const [panel, setPanel] = useState(null);

  const grupper = useMemo(() => {
    const m = new Map();
    rader.forEach((r) => {
      const n = r.ansvarlig || "Unassigned";
      if (!m.has(n)) m.set(n, { navn: n, farge: r.eierFarge, bilde: r.eierBilde || "", eierId: r.eierId || null, rader: [], ferdige: [] });
      (r.ferdig ? m.get(n).ferdige : m.get(n).rader).push(r);
    });
    const liste = [...m.values()].sort((a, b) => (a.navn === "Unassigned") - (b.navn === "Unassigned") || b.rader.length - a.rader.length);
    liste.forEach((g) => g.rader.sort(ukeSort));
    return liste;
  }, [rader]);

  const apne = rader.filter((r) => !r.ferdig);
  const snart = apne.filter((r) => { const d = dagerTil(r.frist); return d !== null && d <= 1; }).length;
  const uten = apne.filter((r) => !r.eierId).length;
  const ferdigeUke = rader.length - apne.length;
  const synlige = eier ? grupper.filter((g) => g.navn === eier) : grupper;

  return (
    <div contentEditable={false}>
      <div className="dok-uke-status" data-testid="dok-uke-status">
        <span><b>{apne.length}</b> åpne oppgaver</span>
        <span className={snart ? "varsle" : ""}><b>{snart}</b> forfaller snart</span>
        {uten > 0 && <span className="varsle"><b>{uten}</b> uten eier</span>}
        {ferdigeUke > 0 && <span><b>{ferdigeUke}</b> fullført denne uken</span>}
        <span className="flex-1" />
        <span className="dok-uke-chips">
          <button type="button" onClick={() => setEier(null)} className={!eier ? "aktiv" : ""} data-testid="dok-uke-eier-alle">Alle</button>
          {grupper.map((g) => (
            <button key={g.navn} type="button" onClick={() => setEier(eier === g.navn ? null : g.navn)}
              className={eier === g.navn ? "aktiv" : ""} data-testid={`dok-uke-eier-${g.navn}`}>{g.navn}</button>
          ))}
        </span>
      </div>
      <div className={`dok-uke-grid${eier ? " en" : ""}`}>
        {synlige.map((g) => (
          <div key={g.navn} className="dok-uke-kol" data-testid={`dok-uke-kol-${g.navn}`}>
            <p className="dok-uke-hode">
              {g.navn === "Unassigned"
                ? <i className="ut">!</i>
                : g.bilde
                  ? <i className="foto"><img src={g.bilde} alt={g.navn} /></i>
                  : <i style={{ background: g.farge || "#8B5CF6" }}>{g.navn[0]}</i>}
              <b>{g.navn}</b>
              <span className="antall">{g.rader.length}</span>
            </p>
            {(eier ? g.rader : g.rader.slice(0, 6)).map((r) => {
              const due = fmtDue(r.frist);
              return (
                <div key={r.id} className="dok-uke-rad" data-testid={`dok-uke-rad-${r.id}`}>
                  <button type="button" className="dok-uke-check" onClick={() => onFullfor(r.id)} title="Marker som ferdig" data-testid={`dok-uke-fullfor-${r.id}`}>
                    <Check size={10} />
                  </button>
                  <button type="button" className="dok-uke-innhold" onClick={() => setPanel(r)} data-testid={`dok-uke-aapne-${r.id}`}>
                    <span className="dok-uke-t">{r.navn}</span>
                    <span className="dok-uke-m">
                      {r.kobling?.label && <>{r.kobling.label} · </>}
                      {r.pri && <em className={`dok-uke-pri p-${r.pri.toLowerCase()}`}>{r.pri}</em>}
                      {due && <span className={`dok-uke-due ${due.tone}`}>{due.label}</span>}
                    </span>
                  </button>
                </div>
              );
            })}
            {!eier && g.rader.length > 6 && (
              <button type="button" className="dok-uke-alle" onClick={() => setEier(g.navn)} data-testid={`dok-uke-mer-${g.navn}`}>
                Vis alle {g.rader.length} <ChevronRight size={10} />
              </button>
            )}
            {(eier ? g.ferdige : g.ferdige.slice(0, 4)).map((r) => (
              <div key={r.id} className="dok-uke-rad ferdig" data-testid={`dok-uke-ferdig-${r.id}`}>
                <button type="button" className="dok-uke-check fylt" onClick={() => onGjenaapne(r.id)} title="Gjenåpne" data-testid={`dok-uke-gjenaapne-${r.id}`}>
                  <Check size={10} />
                </button>
                <button type="button" className="dok-uke-innhold" onClick={() => setPanel(r)}>
                  <span className="dok-uke-t">{r.navn}</span>
                  {r.kobling?.label && <span className="dok-uke-m">{r.kobling.label}</span>}
                </button>
              </div>
            ))}
            <NyUkeRad onNy={(t) => onNy(t, g.eierId)} />
          </div>
        ))}
      </div>
      {panel && (
        <UkePanel rad={panel} onLukk={() => setPanel(null)} navigate={navigate}
          onGjenaapne={(id) => { setPanel(null); onGjenaapne(id); }}
          onFullfor={(id) => { setPanel(null); onFullfor(id); }} />
      )}
    </div>
  );
}

export default function SakerBlokk({ node, updateAttributes, deleteNode, editor }) {
  const spec = node.attrs.spec || {};
  const [rader, setRader] = useState(null);
  const [konfig, setKonfig] = useState(false);
  const [sok, setSok] = useState("");
  const [treff, setTreff] = useState([]);
  const navigate = useNavigate();
  const specStr = JSON.stringify(spec);
  const uke = spec.modus === "uke";

  const last = useCallback(() => {
    hentBlokkData({ ...JSON.parse(specStr), typ: "saker" }, editor.dokId)
      .then(setRader)
      .catch(() => setRader([]));
  }, [specStr, editor]);

  useEffect(() => { last(); }, [last]);

  useEffect(() => {
    if (!konfig || spec.modus !== "kuratert") return undefined;
    const t = setTimeout(() => blokkSok(sok, "tasks").then(setTreff).catch(() => setTreff([])), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sok, konfig, spec.modus]);

  const endre = (e) => updateAttributes({ spec: { ...spec, ...e } });
  const ids = spec.ids || [];

  const fullfoer = async (id) => {
    try {
      await api.put(`/admin/tasks/${id}`, { status: "done" });
      toast.success("Oppgaven er fullført");
      last();
    } catch {
      toast.error("Kunne ikke oppdatere saken");
    }
  };

  const gjenaapne = async (id) => {
    try {
      await api.put(`/admin/tasks/${id}`, { status: "inbox" });
      toast.success("Oppgaven er gjenåpnet");
      last();
    } catch {
      toast.error("Kunne ikke oppdatere saken");
    }
  };

  const nyOppgave = async (tittel, eierId) => {
    try {
      await api.post("/admin/tasks", { title: tittel, assigneeId: eierId, labels: [spec.etikett || "Outreach"], priority: 2, actor: "Dokument" });
      toast.success("Oppgave opprettet");
      last();
    } catch {
      toast.error("Kunne ikke opprette oppgaven");
    }
  };

  return (
    <NodeViewWrapper className={`dok-blokk dok-dock${uke ? " dok-uke dok-blokk-sprint" : ""}${uke && spec.bred ? " dok-blokk-bred" : ""}`} data-testid={`dok-blokk-saker-${spec.bid}`}>
      <div className="dok-blokk-hode" contentEditable={false}>
        <span className="dok-blokk-overskrift">{spec.tittel || (uke ? "Denne uken" : "Saker")}</span>
        {!uke && rader && rader.length > 0 && <span className="dok-dock-antall">{rader.length} kritiske trekk</span>}
        <span className="flex-1" />
        {uke && (
          <button type="button" className="dok-blokk-knapp" onClick={() => endre({ bred: !spec.bred })}
            title={spec.bred ? "Normal bredde" : "Utvid til full bredde"} data-testid={`dok-blokk-uke-bred-${spec.bid}`}>
            {spec.bred ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          </button>
        )}
        <button type="button" className="dok-blokk-knapp" onClick={() => setKonfig((v) => !v)} title="Innstillinger" data-testid={`dok-blokk-saker-konfig-${spec.bid}`}><Settings2 size={12} /></button>
        <button type="button" className="dok-blokk-knapp slett" onClick={deleteNode} title="Fjern blokk" data-testid={`dok-blokk-saker-slett-${spec.bid}`}><X size={12} /></button>
      </div>

      {konfig && (
        <div className="dok-blokk-konfig" contentEditable={false} data-testid="dok-blokk-saker-panel">
          <div className="flex flex-wrap items-center gap-2">
            <input value={spec.tittel || ""} onChange={(e) => endre({ tittel: e.target.value })} placeholder="Blokk-tittel (f.eks. Denne uken)"
              className="dok-blokk-input flex-1 min-w-[140px]" data-testid="dok-blokk-saker-tittel" />
            <div className="dok-blokk-seg">
              <button type="button" onClick={() => endre({ modus: "uke" })} className={uke ? "aktiv" : ""} data-testid="dok-blokk-saker-uke">Denne uken</button>
              <button type="button" onClick={() => endre({ modus: "koblet" })} className={spec.modus !== "kuratert" && !uke ? "aktiv" : ""} data-testid="dok-blokk-saker-koblet">Koblet</button>
              <button type="button" onClick={() => endre({ modus: "kuratert" })} className={spec.modus === "kuratert" ? "aktiv" : ""} data-testid="dok-blokk-saker-kuratert">Håndplukket</button>
            </div>
            {uke ? (
              <input value={spec.etikett || ""} onChange={(e) => endre({ etikett: e.target.value })} placeholder="Etikett-filter (f.eks. Outreach)"
                className="dok-blokk-input min-w-[150px]" data-testid="dok-blokk-saker-etikett" />
            ) : (
              <label className="dok-blokk-kol">
                <input type="checkbox" checked={spec.kunAapne !== false} onChange={() => endre({ kunAapne: spec.kunAapne === false })} data-testid="dok-blokk-saker-kunaapne" />
                Kun åpne
              </label>
            )}
          </div>
          {spec.modus === "kuratert" && (
            <>
              <input value={sok} onChange={(e) => setSok(e.target.value)} placeholder="Søk i saker …"
                className="dok-blokk-input w-full mt-2.5" data-testid="dok-blokk-saker-sok" />
              <div className="dok-blokk-treff">
                {treff.filter((r) => !ids.includes(r.id)).slice(0, 6).map((r) => (
                  <button key={r.id} type="button" onClick={() => endre({ ids: [...ids, r.id] })} data-testid={`dok-blokk-saker-treff-${r.id}`}>
                    <Plus size={11} /><span className="truncate">{r.navn}</span><span className="under">{r.stageLabel}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {rader === null ? (
        <p className="dok-blokk-tom">Henter …</p>
      ) : rader.length === 0 ? (
        <p className="dok-blokk-tom">
          {uke ? "Ingen åpne oppgaver akkurat nå — alt er i mål."
            : spec.modus === "kuratert" ? "Ingen saker valgt ennå." : "Ingen åpne saker er koblet til dokumentet — alt er i mål."}
        </p>
      ) : uke ? (
        <UkeVisning rader={rader} onFullfor={fullfoer} onGjenaapne={gjenaapne} onNy={nyOppgave} navigate={navigate} />
      ) : (
        <div contentEditable={false}>
          {rader.map((r) => (
            <div key={r.id} className="dok-dock-rad" data-testid={`dok-blokk-sak-rad-${r.id}`}>
              <button type="button" className="dok-dock-check" onClick={() => fullfoer(r.id)} title="Marker som fullført" data-testid={`dok-dock-fullfor-${r.id}`}>
                <Check size={11} />
              </button>
              <button type="button" className="flex-1 min-w-0 text-left" onClick={() => navigate(`/admin/saker?aapne=${r.id}`)}>
                <span className="dok-dock-saknavn">{r.navn}</span>
                <span className="dok-dock-sakmeta">
                  {r.ansvarlig
                    ? (r.eierBilde
                      ? <span className="dok-eier-mini foto" title={r.ansvarlig}><img src={r.eierBilde} alt="" /></span>
                      : <span className="dok-eier-mini" style={{ background: r.eierFarge || "#8B5CF6" }} title={r.ansvarlig}>{r.ansvarlig[0]}</span>)
                    : <span className="dok-eier-mini ut" title="Unassigned">!</span>}
                  {r.ansvarlig && <>{r.ansvarlig} · </>}
                  {r.frist && <>Frist {fmtFrist(r.frist)} · </>}
                  <i className="dok-blokk-dot" style={{ background: stageDot(r.stage) }} />{r.stageLabel}
                </span>
                {r.nesteSteg && <span className="dok-dock-next">Next: {r.nesteSteg}</span>}
              </button>
              {spec.modus === "kuratert" && (
                <button type="button" className="dok-blokk-knapp slett" onClick={() => endre({ ids: ids.filter((i) => i !== r.id) })} title="Fjern fra blokken" data-testid={`dok-blokk-sak-fjern-${r.id}`}><X size={11} /></button>
              )}
              <ChevronRight size={12} className="dok-target-pil" />
            </div>
          ))}
          <button type="button" className="dok-dock-alle" onClick={() => navigate("/admin/saker")} data-testid="dok-dock-alle">
            Se alle saker <ChevronRight size={11} />
          </button>
        </div>
      )}
    </NodeViewWrapper>
  );
}
